import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import HandymanIcon from '@mui/icons-material/Handyman';
import LanguageIcon from '@mui/icons-material/Language';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PixIcon from '@mui/icons-material/Pix';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FolderIcon from '@mui/icons-material/Folder';

import BreakdownBlock, { formatNumber, formatDate } from '../breakdownBlock/breakdownBlock';

// Icon per segment — the labels come fixed from the API (segmentLabel)
const SEGMENT_ICONS = {
  'Automação': <PointOfSaleIcon />,
  'Ponto': <FingerprintIcon />,
  'Assistência': <BuildCircleIcon />,
  'Maker': <HandymanIcon />,
  'Site': <LanguageIcon />,
  // PERFIL_PRODUTO = -1 (ou cliente que ainda não chegou pelo sync): não é um
  // sexto segmento, é a ausência de um — por isso o ponto de interrogação
  'Sem segmento': <HelpOutlineIcon />,
};

// The three main segments read by color, so they keep the same identity across
// periods. Values come from the block's validated palette (light / dark).
const SEGMENT_COLORS = {
  'Automação': { light: '#2a78d6', dark: '#3987e5' },  // azul
  'Ponto': { light: '#e34948', dark: '#e66767' },      // vermelho
  'Assistência': { light: '#1baf7a', dark: '#199e70' }, // verde
};

// Icon per payment method — matched on the description registered in Gerente.
// "ENTRADA + ..." comes first: those are split payments, not plain card/slip.
const PAYMENT_ICON_RULES = [
  [/^ENTRADA/i, <CallSplitIcon />],
  [/BRINDE/i, <CardGiftcardIcon />],
  [/CART[ÃA]O|CARTAO/i, <CreditCardIcon />],
  [/BOLETO/i, <ReceiptLongIcon />],
  [/PIX/i, <PixIcon />],
  [/DEP[ÓO]SITO|DEPOSITO/i, <AccountBalanceIcon />],
  [/DINHEIRO|VISTA/i, <PaymentsIcon />],
];

const paymentIcon = (description) => {
  const rule = PAYMENT_ICON_RULES.find(([re]) => re.test(description || ''));
  return rule ? rule[1] : <FolderIcon />;
};

// Turns an API group ({ description, items }) into a category for the block:
// group -> client -> the sales themselves. Same shape the incomes breakdown uses.
const toCategories = (groups, iconFor, colorFor) =>
  (groups || []).map((group) => {
    const byClient = new Map();

    for (const sale of group.items || []) {
      const name = sale.clientName || 'Sem cliente';
      if (!byClient.has(name)) byClient.set(name, { name, value: 0, entries: [] });
      const client = byClient.get(name);
      client.value = +(client.value + (sale.netAmount || 0)).toFixed(2);
      client.entries.push({
        id: sale.id,
        primary: sale.saleNumber ? `Venda ${sale.saleNumber}` : 'Venda sem número',
        secondary: [
          sale.confirmedDate ? formatDate(sale.confirmedDate) : null,
          sale.seller,
          sale.maintenanceValue ? `manutenção ${formatNumber(sale.maintenanceValue)}` : null,
        ].filter(Boolean).join(' · '),
        value: sale.netAmount || 0,
      });
    }

    for (const client of byClient.values()) {
      client.entries.sort((a, b) => b.value - a.value);
    }

    return {
      id: group.description,
      colorKey: group.description,
      name: group.description,
      icon: iconFor(group.description),
      color: colorFor ? colorFor(group.description) : undefined,
      value: group.netAmount || 0,
      children: Array.from(byClient.values()),
    };
  });

export default function SalesBreakdown({ sales }) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

  const segmentCategories = useMemo(
    () => toCategories(
      sales?.bySegment?.groups,
      (name) => SEGMENT_ICONS[name] || <MoreHorizIcon />,
      (name) => SEGMENT_COLORS[name]?.[mode],
    ),
    [sales, mode]
  );

  const paymentCategories = useMemo(
    () => toCategories(sales?.byPaymentMethod?.groups, paymentIcon),
    [sales]
  );

  const totalNetAmount = sales?.totalNetAmount || 0;
  const totalCount = sales?.bySegment?.totalCount || 0;
  const totalMaintenance = sales?.bySegment?.totalMaintenance || 0;

  return (
    <Box>
      {/* Period summary */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Vendas</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{totalCount}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Valor líquido</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(totalNetAmount)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Manutenção gerada</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(totalMaintenance)}</Typography>
        </Box>
      </Box>

      <BreakdownBlock
        title="Vendas por segmento"
        categories={segmentCategories}
        mode={mode}
        emptyLabel="Sem vendas no período"
      />

      <Box sx={{ height: 24 }} />

      <BreakdownBlock
        title="Vendas por forma de pagamento"
        categories={paymentCategories}
        foldBelow={1000}
        mode={mode}
        emptyLabel="Sem vendas no período"
      />
    </Box>
  );
}
